// 🌐 HTTP请求工具类 - 基于axios的统一封装
import axios from 'axios'
import secureStorage from '@/utils/secureStorage'

/**
 * HTTP请求工具类
 * 
 * 功能特性：
 * - 统一的请求配置和基础URL管理
 * - 自动添加认证token到请求头
 * - 请求和响应拦截器，统一处理业务逻辑
 * - 完善的错误处理机制，包含网络错误、HTTP状态码错误、业务错误
 * - 支持文件上传下载，带进度回调
 * - 支持请求取消功能
 * - 开发环境下的请求日志记录
 * - 自动处理401未授权跳转登录
 * 
 * 使用方式：
 * import httpUtil from '@/utils/http'
 * 
 * // GET请求
 * const data = await httpUtil.get('/api/users', { page: 1 })
 * 
 * // POST请求
 * const result = await httpUtil.post('/api/users', { name: '张三' })
 * 
 * // 文件上传
 * const uploadResult = await httpUtil.upload('/api/upload', file, (progress) => {
 *   console.log(`上传进度: ${progress}%`)
 * })
 */
class HttpUtil {
  /**
   * 构造函数 - 初始化axios实例和拦截器
   */
  constructor() {
    // 创建axios实例，配置基础参数
    // 默认后端地址为 localhost，确保与后端的 CORS 允许来源一致
   const defaultBase = '/api'

    this.instance = axios.create({
      baseURL: process.env.VUE_APP_API_BASE_URL || defaultBase,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
})

    // 初始化请求和响应拦截器
    this.initInterceptors()
  }

  /**
   * 初始化请求和响应拦截器
   * 
   * 请求拦截器功能：
   * - 自动添加认证token到请求头
   * - GET请求添加时间戳防止缓存
   * - 开发环境下记录请求日志
   * 
   * 响应拦截器功能：
   * - 统一处理响应数据格式
   * - 自动处理业务错误码
   * - 统一错误处理和日志记录
   */
  initInterceptors() {
    // ========== 请求拦截器 ==========
    this.instance.interceptors.request.use(
      (config) => {
        // 1. 添加认证token到请求头
        // 从localStorage或sessionStorage获取token并添加到Authorization头
        const token = this.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // 2. 保持GET请求参数原样，不再自动追加时间戳，确保与后端严格对齐

        // 3. 开发环境下打印请求日志，便于调试
        if (process.env.NODE_ENV === 'development') {
          const maskedToken = (config.headers.Authorization || '').replace(/^(Bearer\s+)/, '$1')
            .replace(/([A-Za-z0-9_-]{6})[A-Za-z0-9_-]+([A-Za-z0-9_-]{6})/, '$1…$2')
          let fullUrl = config.url
          try {
            fullUrl = new URL(config.url, this.instance.defaults.baseURL).href
          } catch (_) {}
          console.log('🚀 HTTP Request:', {
            url: config.url,
            fullUrl,
            method: config.method,
            params: config.params,
            data: config.data,
            headers: {
              ...config.headers,
              Authorization: maskedToken || undefined
            }
          })
        }

        return config
      },
      (error) => {
        // 请求配置错误处理
        console.error('❌ Request Error:', error)
        return Promise.reject(error)
      }
    )

    // ========== 响应拦截器 ==========
    this.instance.interceptors.response.use(
      (response) => {
        // 1. 开发环境下打印响应日志，便于调试
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ HTTP Response:', {
            url: response.config.url,
            status: response.status,
            data: response.data
          })
        }

        // 2. 统一处理响应数据格式
        const { data } = response

        // 适配统一包装结构：{ success, code, message, data, timestamp }
        if (data && typeof data === 'object' && ('code' in data || 'success' in data)) {
          const isSuccess = data.success === true || data.code === 200 || data.code === 0
          if (isSuccess) {
            // 返回实际数据部分，如果没有data字段则返回整个响应
            return data.data !== undefined ? data.data : data
          }
          // 业务逻辑错误：后端返回了错误码/失败标志
          const error = new Error(data.message || '请求失败')
          error.code = data.code ?? 'BUSINESS_ERROR'
          error.response = response
          throw error
        }

        // 如果不是标准格式，直接返回响应数据
        return data
      },
      (error) => {
        // 3. 统一错误处理
        return this.handleError(error)
      }
    )
  }

  extractValidationErrors(responseData) {
    const errors = responseData?.data?.errors
    if (!Array.isArray(errors)) {
      return []
    }

    return errors
      .map((item) => {
        const rawField = item?.field || item?.property || ''
        const field = typeof rawField === 'string' && rawField.includes('.')
          ? rawField.split('.').pop()
          : rawField

        return {
          field: typeof field === 'string' ? field : '',
          message: item?.message || '格式不正确',
          rejectedValue: item?.rejectedValue ?? item?.['invalidValue']
        }
      })
      .filter((item) => item.field || item.message)
  }

  extractBackendMessage(responseData) {
    if (!responseData || typeof responseData !== 'object') {
      return ''
    }

    const message = typeof responseData.message === 'string' ? responseData.message.trim() : ''
    if (message) {
      return message
    }

    const nestedError = responseData?.data?.error
    return typeof nestedError === 'string' ? nestedError.trim() : ''
  }

  extractBackendDetail(responseData) {
    const detail = responseData?.data?.error
    return typeof detail === 'string' ? detail.trim() : ''
  }

  /**
   * 统一错误处理方法
   * 
   * 处理三种类型的错误：
   * 1. HTTP状态码错误（4xx, 5xx）
   * 2. 网络错误（超时、连接失败等）
   * 3. 其他未知错误
   * 
   * @param {Error} error - axios错误对象
   * @returns {Promise} 返回被拒绝的Promise，包含统一格式的错误信息
   */
  handleError(error) {
    let message
    let code

    if (error.response) {
      // ========== HTTP响应错误 ==========
      // 服务器返回了错误状态码（4xx, 5xx）
      const { status, data } = error.response
      const validationErrors = this.extractValidationErrors(data)
      const backendMessage = this.extractBackendMessage(data)
      const backendDetail = this.extractBackendDetail(data)
      
      switch (status) {
        case 400:
          message = backendMessage || '请求参数错误'
          code = 'BAD_REQUEST'
          break
        case 401:
          message = backendMessage || '未授权，请重新登录'
          code = 'UNAUTHORIZED'
          // 处理401未授权：清除token并跳转登录页
          this.handleUnauthorized()
          break
        case 403:
          message = backendMessage || '拒绝访问，权限不足'
          code = 'FORBIDDEN'
          break
        case 404:
          message = backendMessage || '请求的资源不存在'
          code = 'NOT_FOUND'
          break
        case 422:
          message = validationErrors.length > 0
            ? '提交数据校验失败，请检查表单填写'
            : (backendMessage || '提交数据校验失败，请检查表单填写')
          code = 'VALIDATION_FAILED'
          break
        case 429:
          message = backendMessage || '请求过于频繁，请稍后再试'
          code = 'TOO_MANY_REQUESTS'
          break
        case 500:
          message = backendMessage || '服务器内部错误'
          if (backendDetail && backendDetail !== message) {
            message = `${message}：${backendDetail}`
          }
          code = 'SERVER_ERROR'
          break
        default:
          message = backendMessage || `请求失败 (HTTP ${status})`
          code = `HTTP_${status}`
      }
    } else if (error.request) {
      // ========== 网络错误 ==========
      // 请求已发出但没有收到响应（网络超时、连接失败、CORS拦截等）
      // axios 在超时场景通常设置 error.code = 'ECONNABORTED'
      // 浏览器网络问题通常为 'ERR_NETWORK' 或 message = 'Network Error'
      if (error.code === 'ECONNABORTED') {
        message = '请求超时，请稍后重试'
        code = 'TIMEOUT'
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        message = '网络连接失败或被浏览器拦截（可能为CORS）'
        code = 'NETWORK_ERROR'
      } else {
        message = '网络连接异常，请检查后端服务或跨域配置'
        code = 'NETWORK_ERROR'
      }
    } else {
      // ========== 其他错误 ==========
      // 请求配置错误或其他未知错误
      message = error.message || '未知错误'
      code = 'UNKNOWN'
    }

    // 打印详细的错误日志，便于开发调试
    console.error('❌ HTTP Error:', {
      code,           // 错误代码
      message,        // 错误消息
      url: error.config?.url,     // 请求URL
      method: error.config?.method, // 请求方法
      error           // 原始错误对象
    })

    // 创建统一格式的错误对象
    const customError = new Error(message)
    customError.code = code                    // 错误代码
    customError.originalError = error          // 保留原始错误对象
    customError.status = error.response?.status
    customError.response = error.response
    customError.backendData = error.response?.data
    customError.validationErrors = this.extractValidationErrors(error.response?.data)

    // 特殊处理：429 限流，暴露 Retry-After 以便前端重试
    if (error.response && error.response.status === 429) {
      const headers = error.response.headers || {}
      const retryHeader = headers['retry-after'] || headers['Retry-After']
      if (retryHeader) {
        const sec = parseInt(retryHeader, 10)
        customError.retryAfter = Number.isNaN(sec) ? retryHeader : sec
      }
    }

    return Promise.reject(customError)
  }

  /**
   * 处理401未授权错误
   * 
   * 当接收到401状态码时：
   * 1. 清除本地存储的token
   * 2. 如果当前不在首页登录页，则跳转到首页
   */
  handleUnauthorized() {
    // 清除所有存储的token
    this.removeToken()
    
    // 如果当前页面不是首页（登录页），则跳转到首页
    const currentPath = window.location.pathname || '/'
    const isLoginPage = currentPath === '/'
    if (!isLoginPage) {
      window.location.href = '/'
    }
  }

  /**
   * 获取存储的认证token
   * 
   * 优先从localStorage获取，如果没有则从sessionStorage获取
   * localStorage：持久化存储，浏览器关闭后仍然保留
   * sessionStorage：会话存储，浏览器关闭后清除
   * 
   * @returns {string|null} 返回token字符串，如果没有则返回null
   */
  getToken() {
    // 兼容加密存储与可能存在的旧会话存储
    return secureStorage.get('token') || sessionStorage.getItem('token')
  }

  /**
   * 移除存储的认证token
   * 
   * 同时清除localStorage和sessionStorage中的token
   * 通常在用户登出或token失效时调用
   */
  removeToken() {
    secureStorage.remove('token')
    sessionStorage.removeItem('token')
  }

  // ========== HTTP请求方法 ==========

  /**
   * GET请求
   * 
   * 用于获取数据，参数通过URL查询字符串传递
   * 
   * @param {string} url - 请求URL
   * @param {Object} params - 查询参数对象
   * @param {Object} config - axios配置对象
   * @returns {Promise} 返回Promise，resolve时包含响应数据
   * 
   * 使用示例：
   * const users = await httpUtil.get('/api/users', { page: 1, size: 10 })
   */
  get(url, params = {}, config = {}) {
    return this.instance.get(url, { params, ...config })
  }

  /**
   * POST请求
   * 
   * 用于创建新资源，数据通过请求体传递
   * 
   * @param {string} url - 请求URL
   * @param {Object} data - 请求体数据
   * @param {Object} config - axios配置对象
   * @returns {Promise} 返回Promise，resolve时包含响应数据
   * 
   * 使用示例：
   * const newUser = await httpUtil.post('/api/users', { name: '张三', email: 'zhangsan@example.com' })
   */
  post(url, data = {}, config = {}) {
    return this.instance.post(url, data, config)
  }

  /**
   * PUT请求
   * 
   * 用于更新整个资源，数据通过请求体传递
   * 
   * @param {string} url - 请求URL
   * @param {Object} data - 请求体数据
   * @param {Object} config - axios配置对象
   * @returns {Promise} 返回Promise，resolve时包含响应数据
   * 
   * 使用示例：
   * const updatedUser = await httpUtil.put('/api/users/123', { name: '李四', email: 'lisi@example.com' })
   */
  put(url, data = {}, config = {}) {
    return this.instance.put(url, data, config)
  }

  /**
   * DELETE请求
   * 
   * 用于删除资源
   * 
   * @param {string} url - 请求URL
   * @param {Object} config - axios配置对象
   * @returns {Promise} 返回Promise，resolve时包含响应数据
   * 
   * 使用示例：
   * await httpUtil.delete('/api/users/123')
   */
  delete(url, config = {}) {
    return this.instance.delete(url, config)
  }

  /**
   * PATCH请求
   * 
   * 用于部分更新资源，数据通过请求体传递
   * 
   * @param {string} url - 请求URL
   * @param {Object} data - 请求体数据
   * @param {Object} config - axios配置对象
   * @returns {Promise} 返回Promise，resolve时包含响应数据
   * 
   * 使用示例：
   * const updatedUser = await httpUtil.patch('/api/users/123', { email: 'newemail@example.com' })
   */
  patch(url, data = {}, config = {}) {
    return this.instance.patch(url, data, config)
  }

  // ========== 文件操作方法 ==========

  /**
   * 上传文件
   * 
   * 支持单文件上传，自动设置multipart/form-data格式
   * 可选的上传进度回调函数
   * 
   * @param {string} url - 上传接口URL
   * @param {File} file - 要上传的文件对象
   * @param {Function} onProgress - 上传进度回调函数，参数为进度百分比(0-100)
   * @returns {Promise} 返回Promise，resolve时包含上传结果
   * 
   * 使用示例：
   * const result = await httpUtil.upload('/api/upload', file, (progress) => {
   *   console.log(`上传进度: ${progress}%`)
   * })
   */
  upload(url, file, onProgress = null) {
    // 创建FormData对象，用于文件上传
    const formData = new FormData()
    formData.append('file', file)

    return this.instance.post(url, formData, {
      headers: {
        // 设置为multipart/form-data，让浏览器自动设置boundary
        'Content-Type': 'multipart/form-data'
      },
      // 上传进度监听
      onUploadProgress: (progressEvent) => {
        if (onProgress && typeof onProgress === 'function') {
          // 计算上传进度百分比
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          onProgress(percentCompleted)
        }
      }
    })
  }

  /**
   * 下载文件
   * 
   * 下载文件并自动触发浏览器下载
   * 支持自定义文件名
   * 
   * @param {string} url - 下载文件的URL
   * @param {string} filename - 保存的文件名，如果不提供则使用默认名称
   * @returns {Promise} 返回Promise，resolve时表示下载完成
   * 
   * 使用示例：
   * await httpUtil.download('/api/files/report.pdf', '报告.pdf')
   */
  download(url, filename = null, params = {}) {
    return this.instance.get(url, {
      // 设置响应类型为blob，用于处理二进制文件
      responseType: 'blob',
      params,
      headers: {
        Accept: '*/*'
      }
    }).then(response => {
      const isAxiosResponse = response && typeof response === 'object' && 'data' in response
      const rawBlob = isAxiosResponse ? response.data : response
      const blob = rawBlob instanceof Blob ? rawBlob : new Blob([rawBlob])
      // 解析文件名（优先使用响应头的 Content-Disposition）
      const headers = isAxiosResponse ? (response.headers || {}) : {}
      const disposition = headers['content-disposition'] || headers['Content-Disposition']
      let resolvedFilename = filename || 'download'
      if (disposition) {
        const match = /filename[^;=\n]*=((['"])?.*?\2|[^;\n]*)/.exec(disposition)
        if (match && match[1]) {
          resolvedFilename = decodeURIComponent(String(match[1]).replace(/['"]/g, ''))
        }
      }

      // 创建下载链接
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = resolvedFilename
      
      // 触发下载
      document.body.appendChild(link)
      link.click()
      
      // 清理DOM和内存
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    })
  }

  // ========== 请求取消功能 ==========

  /**
   * 取消请求
   * 
   * 取消正在进行的HTTP请求
   * 
   * @param {Object} cancelToken - 由createCancelToken()方法创建的取消令牌
   * 
   * 使用示例：
   * const cancelToken = httpUtil.createCancelToken()
   * const request = httpUtil.get('/api/data', {}, { cancelToken: cancelToken.token })
   * // 5秒后取消请求
   * setTimeout(() => httpUtil.cancelRequest(cancelToken), 5000)
   */
  cancelRequest(cancelToken) {
    if (cancelToken) {
      cancelToken.cancel('请求已取消')
    }
  }

  /**
   * 创建取消令牌
   * 
   * 创建一个可以用于取消请求的令牌对象
   * 
   * @returns {Object} 返回包含token和cancel方法的对象
   * 
   * 使用示例：
   * const cancelToken = httpUtil.createCancelToken()
   * const request = httpUtil.get('/api/data', {}, { cancelToken: cancelToken.token })
   * // 需要取消时调用
   * httpUtil.cancelRequest(cancelToken)
   */
  createCancelToken() {
    return axios.CancelToken.source()
  }
}

// ========== 导出配置 ==========

// 创建HTTP工具类的单例实例
// 整个应用共享同一个实例，确保配置和拦截器的一致性
const httpUtil = new HttpUtil()

// 默认导出：单例实例，推荐使用方式
// 使用方式：import httpUtil from '@/utils/http'
export default httpUtil

// 命名导出：类本身，用于需要创建多个实例的场景
// 使用方式：import { HttpUtil } from '@/utils/http'
export { HttpUtil }

// 便捷方法导出：直接导出常用的HTTP方法
// 使用方式：import { get, post, put, del } from '@/utils/http'
// 注意：delete是JavaScript关键字，所以导出为del
export const { get, post, put, delete: del, patch, upload, download } = httpUtil
